import React, {Button, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Grid, Box, makeStyles } from '@material-ui/core';
import {
  MaximizeButton,
  DateButton,
  OptionsButton,
  MenuButton,
  DeleteButton
} from 'components/buttons/ActionButtons';
import WidgetTitle from 'components/Typography/WidgetTitle';
import { calculateDynamicTimeIntervals } from 'utils/widget-helpers';

const useStyles = makeStyles(theme => ({
  box: {
    padding: theme.customStyles.baseWidgetBox.padding,
    height: theme.customStyles.baseWidget.height
  },
  subTitles: {
    fontSize: theme.customStyles.baseWdigetDates.fontSize
  }
}));

const BaseWidget = ({
  children,
  widget,
  onDateSettingsSubmit,
  onMainSettingsSubmit,
  onMoreOptionsSubmit,
  dynamicTitle,
  dashboardData,
  tableExportData,
  widgetRef,
  isEditMode,
  dispatchDashboard,
  shouldDisplayDates,
  state,
  setFilters,
  globalPickerDateState
}) => {
  const {
    id,
    startDate,
    endDate,
    title,
    description,
    dateRangeTypeId,
    dateMatchType,
    customDateMatchType,
    masterObjectId,
    masterObjectTypeId,
    benchmarks,
    isTitleDisplayed,
    isDateDisplayed,
    isLegendDisplayed,
    isChartTooltipDisplayed,
    overlay,
    timeSeriesSetId,
  } = widget;

  const { widgetTitle, widgetOverlayLists } = overlay;
  const classes = useStyles();
  const [startDateState, setStartDateState] = useState(startDate);
  const [endDateState, setEndDateState] = useState(endDate);

  const isDynamicDate = widget && startDate === null && endDate === null;
  const dynamicDateParams = isDynamicDate && calculateDynamicTimeIntervals(dateRangeTypeId);
  const { startDate: dashboardStartDate, endDate: dashboardEndDate } = dashboardData;

  const { startPeriod, endPeriod } = dynamicDateParams || {
    startPeriod: dashboardStartDate,
    endPeriod: dashboardEndDate
  };

  useEffect(() => {
    if (globalPickerDateState && globalPickerDateState.startDate && globalPickerDateState.endDate) {
      const start = moment(globalPickerDateState.startDate).format('YYYY-MM-DDTHH:mm:ss');
      const end = moment(globalPickerDateState.endDate).format('YYYY-MM-DDTHH:mm:ss');
      setStartDateState(start);
      setEndDateState(end);
    } else if (globalPickerDateState && globalPickerDateState.dateRangeTypeId) {
      const calculatedDatePeriods = calculateDynamicTimeIntervals(globalPickerDateState.dateRangeTypeId);
      const start = moment(calculatedDatePeriods.startPeriod, 'MM.DD.YYYY').format('YYYY-MM-DD');
      const end = moment(calculatedDatePeriods.endPeriod, 'MM.DD.YYYY').format('YYYY-MM-DD');
      setStartDateState(start);
      setEndDateState(end);
    }
  }, [globalPickerDateState]);

  //this logic can be changed if actualStartDate and actualEndDate come null when there is no data from the BE.
  if(!state) return null
  const { data } = state;

  const compareDates = (date1, date2) => {
    if (!date1 && !date2) {
      if (startPeriod && !startDateState) {
        const datesString = startPeriod === 'Since Inception' ? startPeriod : `Filters ${formatPeriodDate(startPeriod)} to ${formatPeriodDate(endPeriod)}`;
        return datesString;
      } else {
        return <div>{`Filters ${formatDate(startDateState)} to ${formatDate(endDateState)}`}</div>
      }
    }
    return date1 === date2 ? formatDate(date1) : formatDate(date1) + ' to ' + formatDate(date2);
  }

  const formatPeriodDate = date => moment(date, 'MM.DD.YYYY').format('MMM YYYY');

  const formatDate = date => moment(date).format('MMM YYYY');

  const setDatePickerDates = (start, end) => {
    const momentStart = moment(start);
    const momentEnd = moment(end);
    setStartDateState(momentStart.format('YYYY-MM-DDTHH:mm:ss'));
    setEndDateState(momentEnd.format('YYYY-MM-DDTHH:mm:ss'));
  }

  const getActualDates = () => {
    let startActual;
    let endActual;

    if (data && data.startDateActual && data.endDateActual) {
      startActual = data.startDateActual;
      endActual = data.endDateActual;
    }
    else if (data && data.rows && data.rows.length > 0) {
      startActual = data.rows[0].startDateActual;
      endActual = data.rows[0].endDateActual;
    } else if (data && data.datasets && data.datasets.length > 0) {
      startActual = data.datasets[0].startDateActual;
      endActual = data.datasets[0].endDateActual;
    }

    return {
      actualStartDate: startActual,
      actualEndDate: endActual
    }
  }

  const { actualStartDate, actualEndDate } = getActualDates();

  return (
    <Box
      className={classes.box}
      width="100%"
      p={2}
      boxShadow={2}
      borderTop={3}
      borderColor="#346C9F"
      height="100%"
    >
      <Grid container direction="row" justify="space-between" alignItems="flex-start">
        <Grid item xs={6} md={6} lg={7}>
          {isTitleDisplayed && <WidgetTitle>{dynamicTitle || title || widgetTitle}</WidgetTitle>}
          <>
            <div className={classes.subTitles}>
              {compareDates(actualStartDate, actualEndDate)}
            </div>
          </>
        </Grid>
        <Grid item container direction="row" justify="flex-end" xs={6} md={6} lg={5}>
          {isEditMode && (
            <Grid item md="auto">
              <DeleteButton id={id} dispatchDashboard={dispatchDashboard} />
            </Grid>
          )}
          <Grid item md="auto">
            <MaximizeButton widgetRef={widgetRef} id={masterObjectId}>
              {children}
            </MaximizeButton>
          </Grid>
          {shouldDisplayDates && (
            <Grid item md="auto">
              <DateButton
                startDate={startDate}
                endDate={endDate}
                onSubmit={onDateSettingsSubmit}
                dateRangeTypeId={dateRangeTypeId}
                dateMatchType={dateMatchType}
                customDateMatchType={customDateMatchType}
                isDynamicDate={isDynamicDate}
                startPeriod={startPeriod}
                endPeriod={endPeriod}
                globalPickerDateState={globalPickerDateState}
                setBaseWidgetDates={setDatePickerDates}
              />
            </Grid>
          )}
          <Grid item md="auto">
            <OptionsButton
              widgetRef={widgetRef}
              isTitleDisplayed={isTitleDisplayed}
              isDateDisplayed={isDateDisplayed}
              isLegendDisplayed={isLegendDisplayed}
              isChartTooltipDisplayed={isChartTooltipDisplayed}
              title={dynamicTitle || title || overlay.widgetTitle}
              description={description}
              masterObjectId={masterObjectId}
              masterObjectTypeId={masterObjectTypeId}
              benchmarks={benchmarks}
              tableExportData={tableExportData}
              onMainSettingsSubmit={onMainSettingsSubmit}
              onMoreOptionsSubmit={onMoreOptionsSubmit}
              dashboardData={dashboardData}
              timeSeriesSetId={timeSeriesSetId}
              widgetOverlayLists={widgetOverlayLists}
              setFilters={setFilters}
            />
          </Grid>
          <Grid item md="auto">
            <MenuButton description={description} />
          </Grid>
        </Grid>
      </Grid>
      {children}
    </Box>
  );
};

BaseWidget.propTypes = {
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({})),
      totals: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }),
  onDateSettingsSubmit: PropTypes.func.isRequired,
  onMainSettingsSubmit: PropTypes.func.isRequired,
  onMoreOptionsSubmit: PropTypes.func.isRequired,
  children: PropTypes.node,
  widget: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    dateRangeType: PropTypes.string,
    dateMatchType: PropTypes.number,
    customDateMatchType: PropTypes.number,
    timeSeriesSetId: PropTypes.number,
    isTitleDisplayed: PropTypes.bool.isRequired,
    isDateDisplayed: PropTypes.bool.isRequired,
    isLegendDisplayed: PropTypes.bool.isRequired,
    isChartTooltipDisplayed: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    dateRangeTypeId: PropTypes.number.isRequired,
    masterObjectId: PropTypes.number.isRequired,
    masterObjectName: PropTypes.string.isRequired,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired
      })
    ),
    masterObjectTypeId: PropTypes.number,
    overlay: PropTypes.shape({
      widgetTitle: PropTypes.string,
      publicId: PropTypes.number,
      baseWidget: PropTypes.shape({
        name: PropTypes.string.isRequired,
        widgetType: PropTypes.shape({
          name: PropTypes.string.isRequired
        }).isRequired
      }).isRequired
    }).isRequired
  }).isRequired,
  dashboardData: PropTypes.shape({
    masterObjectId: PropTypes.number.isRequired,
    masterObjectTypeId: PropTypes.number.isRequired,
    masterObjectName: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        benchmarkId: PropTypes.number.isRequired
      })
    )
  }),
  tableExportData: PropTypes.shape({
    title: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.shape({})),
    rows: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({}),
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
      ])
    )
  }),
  dynamicTitle: PropTypes.string,
  widgetRef: PropTypes.shape({}),
  isEditMode: PropTypes.bool,
  shouldDisplayDates: PropTypes.bool,
  dispatchDashboard: PropTypes.func.isRequired
};

BaseWidget.defaultProps = {
  dashboardData: {},
  tableExportData: null,
  children: null,
  widgetRef: null,
  isEditMode: false,
  dynamicTitle: null,
  shouldDisplayDates: true,
  state: {},
};

export default BaseWidget;
